import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';

import { Banner } from '../../elements/Banner/Banner';
import { FloatingBannerImage } from '../../elements/Banner/FloatingBannerImage/FloatingBannerImage';
import type { FloatingBannerImageProps } from '../../elements/Banner/types';
import * as styles from './StandardBanner.styles';

export interface StandardBannerProps {
  backgroundColor: string;
  copy?: string;
  heading: string;
  imageAlt: string;
  imageHeight: number;
  imageLinkHref?: string;
  imageWidth: number;
  images: FloatingBannerImageProps['images'];
  lazyImages?: boolean;
  linkHref?: string;
  linkText?: string;
  onImageLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onVisible?: () => void;
}

export const StandardBanner = ({
  backgroundColor,
  copy,
  heading,
  imageAlt,
  imageHeight,
  imageLinkHref,
  imageWidth,
  images,
  lazyImages,
  linkHref,
  linkText,
  onImageLinkClick,
  onLinkClick,
  onVisible = () => {},
}: StandardBannerProps) => {
  const observedElementRef = useImpressionRef<HTMLDivElement>(() => onVisible());
  const removeImageLinkFromTabIndex = imageLinkHref === linkHref;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onLinkClick?.(e);
  };

  const Image = () => (
    <FloatingBannerImage
      imageAlt={imageAlt}
      imageHeight={imageHeight}
      imageWidth={imageWidth}
      images={images}
      lazyImages={lazyImages}
      offset="bottom"
    />
  );

  return (
    <div css={styles.bottomOffsetImageMarginContainer} ref={observedElementRef}>
      <Banner backgroundColor={backgroundColor} roundedCorners>
        <div css={styles.container} data-testid="standard-banner-content">
          {imageLinkHref ? (
            <a
              css={styles.imageWrapper}
              href={imageLinkHref}
              onClick={onImageLinkClick}
              {...(removeImageLinkFromTabIndex ? { tabIndex: -1 } : {})}
            >
              <Image />
            </a>
          ) : (
            <div css={styles.imageWrapper}>
              <Image />
            </div>
          )}
          <div css={styles.content}>
            <h3 css={styles.heading}>{heading}</h3>
            {copy && <p css={styles.copy}>{copy}</p>}
            {linkHref && linkText && (
              <a css={styles.cta} href={linkHref} onClick={handleClick}>
                {linkText}
              </a>
            )}
          </div>
        </div>
      </Banner>
    </div>
  );
};
